/**
 * What the Finance Agent said, beside the figures it said it about (AGENTS.md §3 v2).
 *
 * The words are the model's; every number on the page is code's. The card names the run it came
 * from and when, and says so — a reader must always be able to tell the narration from the
 * figures (CLAUDE.md §3.6, §12: AI-DERIVED beside CALCULATED, never in place of it).
 */

import type { ReactNode } from 'react';
import type { FinanceNarrationResponse, NarrationItemJson } from '../api.js';

interface NarrationCardProps {
  title: string;
  narration: FinanceNarrationResponse['narration'] | undefined;
  items: readonly NarrationItemJson[];
  /** What to say when the run said nothing about this. */
  quiet: string;
}

const SEVERITY_CLASS: Record<string, string> = {
  high: 'status status-conflict',
  medium: 'status status-pending',
  low: 'status status-approved',
};

export function NarrationCard({ title, narration, items, quiet }: NarrationCardProps): ReactNode {
  if (narration === undefined) return null;
  return (
    <section className="card narration">
      <h2>{title}</h2>
      {narration === null ? (
        <p className="muted small">
          No narration yet. The Finance Agent writes one each morning at 06:30 Eastern, after the
          cash-reserve check, when its flag is on.
        </p>
      ) : (
        <>
          {items.length === 0 ? (
            <p className="muted">{quiet}</p>
          ) : (
            items.map((item, index) => (
              <article key={`${item.kind}:${item.line_key ?? item.month ?? index}`}>
                <h3>
                  <span className={SEVERITY_CLASS[item.severity] ?? 'status'}>{item.severity}</span>{' '}
                  {item.title}
                  {item.month && <span className="muted small"> · {item.month}</span>}
                </h3>
                <p>{item.narrative}</p>
                <ul className="small">
                  {item.why.map((why) => (
                    <li key={why}>{why}</li>
                  ))}
                </ul>
                <p className="muted small">
                  Sources: {item.data_sources.join(', ')} · confidence {item.confidence}
                </p>
              </article>
            ))
          )}
          <p className="muted small">
            AI narration by the Finance Agent, {new Date(narration.generated_at).toLocaleString()} ·
            run {narration.run_id.slice(0, 8)} · {narration.model ?? 'model unrecorded'} · the
            figures on this page are computed by code; these words are about them, not the source of
            them.
          </p>
        </>
      )}
    </section>
  );
}
